import { Injectable } from '@angular/core';
import { ApiRequestsService } from './api-requests.service';
import { Currency } from '../models/currency.model';
import { InvestmentZone } from '../models/investment-zone.model';
import { Benchmark } from '../models/benchmark.model';
import { Product } from '../models/product.model';
import { Frequency } from '../models/frequency.model';
import { MarketPositions } from '../models/market-positions.model';
import { AssetType } from '../models/asset-type.model';
import { DeconcentrationAnalysis } from '../models/analysis/deconcentration-analysis.model';
import { PeerAnalysis } from '../models/analysis/peer-analysis.model';
import { StrategyType } from '../models/strategy-type.model';

@Injectable({
  providedIn: 'root'
})
export class ListsService {

  currencyList:Currency[] = [];
  investmentZoneList:InvestmentZone[] = [];
  benchmarkList:Benchmark[] = [];
  productList:Product[] = [];
  frequencyList:Frequency[] = [
    {alias: 'd', name: 'Daily'},
    {alias: 'w', name: 'Weekly'},
  ] as Frequency[];
  marketPositionsList:MarketPositions[] = [
    {alias: 'LO', name: 'Long Only'},
    {alias: 'LS', name: 'Long Short'},
  ] as MarketPositions[];
  assetTypeList:AssetType[] = [
    {alias: 'TR', name: 'Total return'},
    {alias: 'NR', name: 'Net return'},
    {alias: 'PR', name: 'Price return'},
  ] as AssetType[];
  strategyTypeList:StrategyType[] = [
    {alias: 'MinVar', name: 'Min Variance',  customizable_exposure: false, customizable_risk_contributions: false, customizable_volatility: true, enabled: true},
    {alias: 'EFRP',   name: 'Risk Parity',   customizable_exposure: false, customizable_risk_contributions: true , customizable_volatility: true, enabled: true},
    {alias: 'EFW',    name: 'Equal Weights', customizable_exposure: true , customizable_risk_contributions: false, customizable_volatility: true, enabled: true},
  ] as StrategyType[];
  macroVariablesList = [
    {alias: 'risk-tolerance', name: 'Risk Tolerance'},
    {alias: 'macro-outlook', name: 'Macro Outlook'},
    {alias: 'macro-stability', name: 'Macro Stability'},
    {alias: 'risk-on-off', name: 'Risk On conditions'},
  ];
  stateVariablesList = [
    {alias: 'systematic-volatility', name: 'Market Risk'},
    {alias: 'short-interest', name: 'Short-Term Interest Rate'},
    {alias: 'term-spread', name: 'Interest Rate Spread'},
    {alias: 'default-spread', name: 'Default Spread'},
    {alias: 'dividend-yield', name: 'Dividend Yield'},
    {alias: 'aggregate-spread', name: 'Liquidity Risk (bid-ask spread)'},
    {alias: 'aggregate-impact', name: 'Liquidity Risk (trading impact)'},
  ];
  peerList:DeconcentrationAnalysis[] = [];

  constructor(private apiRequestsService: ApiRequestsService) {
    //this.getCurrencyList();
    //this.getInvestmentZoneList();
    //this.getBenchmarkList();
    //this.getProductList();
  }

  async loadCurrencyList() {
    // If nt loaded already, the list of currencies is loaded from database
    if (this.currencyList.length==0) {
      const data: any = await this.apiRequestsService.get('currency-list');
      if (data.status=='OK') {
        this.currencyList = data.list as Currency[];
      }
    }
  }

  async loadInvestmentZoneList() {
    // If not loaded already, the list of investment zones is loaded from database
    if (this.investmentZoneList.length==0) {
      const data: any = await this.apiRequestsService.get('investment-zone-list');
      if (data.status=='OK') {
        this.investmentZoneList = data.list as InvestmentZone[];
      }
    }
  }

  async loadBenchmarkList() {
    // If not loaded already, the list of benchmarks is loaded from database
    if (this.benchmarkList.length==0) {
      const data: any = await this.apiRequestsService.get('benchmark-list');
      if (data.status=='OK') {
        this.benchmarkList = data.list as Benchmark[];
      }
    }
  }

  async loadProductList() {
    // If not loaded already, the list of products is loaded from database
    if (this.productList.length==0) {
      const data: any = await this.apiRequestsService.get('product-list');
      if (data.status=='OK') {
        data.list.forEach(product_data => {
          this.cleanData(product_data);
        });
        this.productList = data.list as Product[];
      }
    }
  }

  async loadPeerList() {
    // If not loaded already, the list of products is loaded from database
    if (this.peerList.length==0) {
      const data: any = await this.apiRequestsService.post('peer-list',{investment_zone_alias:'USA'},false);
      if (data.status=='OK') {
        this.peerList = data.list as DeconcentrationAnalysis[];
      }
    }
  }

  async loadPeerProduct(product_id: number) {
    // Load peer analysis from database for a specific fund
    const data: any = await this.apiRequestsService.post('peer-product',{product_id: product_id},false);

    if (data.status=='OK') {
      let peer_analysis = new PeerAnalysis();
      peer_analysis.initFromData(data.data);
      return peer_analysis;
    }
  }

  async loadLists(load_products: boolean = true) {
    await this.loadCurrencyList();
    await this.loadInvestmentZoneList();
    await this.loadBenchmarkList();
    if (load_products) {
      await this.loadProductList();
    }
  }

  getCurrencyList()        {return this.currencyList;}
  getInvestmentZoneList()  {return this.investmentZoneList;}
  getBenchmarkList()       {return this.benchmarkList;}
  getProductList()         {return this.productList;}
  getFrequencyList()       {return this.frequencyList;}
  getMarketPositionsList() {return this.marketPositionsList;}
  getAssetTypeList()       {return this.assetTypeList;}
  getStrategyTypeList()    {return this.strategyTypeList;}
  getMacroVariablesList()  {return this.macroVariablesList;}
  getStateVariablesList()  {return this.stateVariablesList;}

  getInvestmentZone(field:string, value:any) {
    return this.investmentZoneList.filter(
      (investmentZone:InvestmentZone) => {
        return investmentZone[field]==value
      }
    )[0]
  }
  getDefaultInvestmentZone() {
    return this.investmentZoneList[0]
  }

  getCurrency(field:string, value:any) {
    return this.currencyList.filter(
      (currency:Currency) => {
        return currency[field]==value
      }
    )[0]
  }
  getDefaultCurrency() {
    return this.currencyList[0]
  }

  getFrequency(field:string, value:any) {
    return this.frequencyList.filter(
      (frequency:Frequency) => {
        return frequency[field]==value
      }
    )[0]
  }

  getMarketPositions(field:string, value:any) {
    return this.marketPositionsList.filter(
      (marketPositions:MarketPositions) => {
        return marketPositions[field]==value
      }
    )[0]
  }
  getDefaultMarketPositions() {
    return this.marketPositionsList[0]
  }

  getAssetType(field:string, value:any) {
    return this.assetTypeList.filter(
      (assetType:AssetType) => {
        return assetType[field]==value
      }
    )[0]
  }

  getStrategyType(field:string, value:any) {
    return this.strategyTypeList.filter(
      (strategyType:StrategyType) => {
        return strategyType[field]==value
      }
    )[0]
  }

  async cleanData(data: any) {
    if (data==undefined) {return}

    await this.loadCurrencyList();
    await this.loadInvestmentZoneList();

    const keys = Object.keys(data);
    for (let index=0; index<keys.length; index++) {
      const key = keys[index];
      if (key === 'frequency_alias') {
        data.frequency = this.getFrequency('alias',data.frequency_alias);
        delete data.frequency_alias;
      } else if (key === 'currency_id') {
        data.currency = this.getCurrency('id',data.currency_id);
        delete data.currency_id;
      } else if (key === 'investment_zone_id') {
        data.investment_zone = this.getInvestmentZone('id',data.investment_zone_id);
        delete data.investment_zone_id;
      } else if (key === 'market_positions_alias') {
        data.market_positions = this.getMarketPositions('alias',data.market_positions_alias);
        delete data.market_positions_alias;
      } else if (key === 'strategy_type_alias') {
        data.strategy_type = this.getStrategyType('alias',data.strategy_type_alias);
        delete data.strategy_type_alias;
      }
    };
  }

  async cleanDataBlocking(data: any) {
    if (data==undefined) {return}

    const keys = Object.keys(data);
    for (let index=0; index<keys.length; index++) {
      const key = keys[index];
      if (key === 'frequency_alias') {
        data.frequency = this.getFrequency('alias',data.frequency_alias);
        delete data.frequency_alias;
      } else if (key === 'currency_id') {
        data.currency = this.getCurrency('id',data.currency_id);
        delete data.currency_id;
      } else if (key === 'investment_zone_id') {
        data.investment_zone = this.getInvestmentZone('id',data.investment_zone_id);
        delete data.investment_zone_id;
      } else if (key === 'market_positions_alias') {
        data.market_positions = this.getMarketPositions('alias',data.market_positions_alias);
        delete data.market_positions_alias;
      } else if (key === 'strategy_type_alias') {
        data.strategy_type = this.getStrategyType('alias',data.strategy_type_alias);
        delete data.strategy_type_alias;
      }
    };
  }

}
